import {
	data as json,
	type HeadersFunction,
	type MetaFunction,
} from 'react-router'
import { ArrowLink } from '#app/components/arrow-button.tsx'
import { ButtonLink } from '#app/components/button.tsx'
import { CourseCard } from '#app/components/course-card.tsx'
import { Grid } from '#app/components/grid.tsx'
import { HeroSection } from '#app/components/sections/hero-section.tsx'
import { H6, Paragraph } from '#app/components/typography.tsx'
import { type KCDHandle } from '#app/types.ts'
import { reuseUsefulLoaderHeaders } from '#app/utils/misc.ts'
import { getServerTimeHeader } from '#app/utils/timing.server.ts'
import { type Route } from './+types/work'

export const handle: KCDHandle = {
	getSitemapEntries: () => [{ route: '/work' }],
}

const doctorsCard = {
	title: 'Doctor Finder & Instant Booking',
	description:
		'From city + specialty search to a confirmed hospital slot - without dumping patients onto a phone tree. Marketplace UX on top; real booking systems underneath.',
	label: 'Conversion systems',
	courseUrl: '/work/doctor-finder-instant-booking',
	ctaLabel: 'Read the case study',
	imageSrc: '/images/projects/doctors/cover-blue.webp',
	imageAlt: 'Doctor discovery product graphic',
	imageClassName: 'object-contain',
} as const

const schoolCard = {
	title: 'OurCampusBD',
	description:
		'A live multi-tenant school OS for Bangladesh: biometric attendance, bKash/Nagad fees, exams, Bangla parent SMS. 22+ institutions, recurring revenue past ৳1 lakh.',
	label: 'Live SaaS · Bangladesh',
	courseUrl: '/work/ourcampusbd-school-management',
	ctaLabel: 'Read the case study',
	imageSrc: '/images/projects/school/cover-yellow.webp',
	imageAlt: 'OurCampusBD school management graphic',
	imageClassName: 'object-contain',
} as const

const videoCard = {
	title: 'Secure Video Hosting at Scale',
	description:
		'Gated, branded streaming without owning a CDN farm - durable unlisted storage, a lean streaming middle tier, and access rules your product actually owns.',
	label: 'Scale without bill shock',
	courseUrl: '/work/secure-youtube-video-hosting',
	ctaLabel: 'Read the case study',
	imageSrc: '/images/projects/secure-video/cover-red.webp',
	imageAlt: 'Secure video streaming graphic',
	imageClassName: 'object-contain',
} as const

const workProducts = [
	{ id: 'doctors', card: { ...doctorsCard, horizontal: true } },
	{ id: 'ourcampus', card: schoolCard },
	{ id: 'secure-video', card: videoCard },
] as const

export async function loader({ request }: Route.LoaderArgs) {
	const timings = {}
	const socialMetas = (
		await import('#app/og/page-meta.server.ts')
	).buildPageSocialMetasForRequest(request, {
		title: 'Work by Hamidul Islam',
		description:
			'Get really good at shipping product systems with Hamidul Islam',
		keywords:
			'Product systems, SaaS, growth engineering, TypeScript, Go, case studies',
		image: '/images/heroes/work.jpg',
		imageAlt: 'Work by Hamidul Islam',
	})

	return json(
		{ socialMetas },
		{
			headers: {
				'Cache-Control': 'public, max-age=3600',
				'Server-Timing': getServerTimeHeader(timings),
			},
		},
	)
}

export const headers: HeadersFunction = reuseUsefulLoaderHeaders

export const meta: MetaFunction<typeof loader> = ({ data }) =>
	data?.socialMetas ?? []

function WorkHome({}: Route.ComponentProps) {
	const featured = workProducts[0]!
	const supporting = workProducts.slice(1)

	return (
		<>
			<HeroSection
				title="Work that grows the business."
				subtitle="Real products. Real constraints. Case studies written so you can feel the problem, the system shape, and why the outcome was worth building."
				imageProps={{
					src: '/images/heroes/work.png',
					alt: 'Product systems growth illustration',
					className: 'object-contain',
					style: { aspectRatio: '1' },
				}}
				imageSize="large"
				action={
					<div className="mr-auto flex flex-col gap-4">
						<ButtonLink to="https://cal.com/hamidxcal" variant="primary">
							Talk about your product
						</ButtonLink>
						<ButtonLink to="/connect" variant="secondary" prefetch="intent">
							Connect
						</ButtonLink>
					</div>
				}
			/>

			<Grid as="main" className="mb-48">
				<div className="col-span-full mb-12 hidden lg:col-span-4 lg:mb-0 lg:block">
					<H6 as="h2">{`Who this is for`}</H6>
				</div>
				<div className="col-span-full mb-8 lg:col-span-4 lg:mb-20">
					<H6 as="h3" className="mb-4">
						{`Founders and teams shipping under pressure`}
					</H6>
					<Paragraph className="mb-20">
						{`
              You need product systems that remove friction, protect margins,
              and scale without a surprise infra bill. Each case study is a
              full story: stakes, approach, system shape, and results - not a
              feature brochure.
            `}
					</Paragraph>
					<H6 as="h3" className="mb-4">
						{`Business problem first. Durable system second.`}
					</H6>
					<Paragraph>
						{`
              Conversion leaks, expensive media, brittle integrations, slow
              products - I map those to systems you can ship, measure, and keep
              fast under real load. Premium is clarity under pressure.
            `}
					</Paragraph>
				</div>
				<div className="col-span-2 col-start-11 hidden items-start justify-end lg:flex">
					<ArrowLink to="#work" direction="down" />
				</div>
			</Grid>

			<h2 className="sr-only" id="work">
				Work
			</h2>

			<Grid className="@container/grid mb-48 grid-cols-12! gap-6 md:gap-6 xl:gap-8">
				<div className="@container col-span-full">
					<CourseCard {...featured.card} />
				</div>
				{supporting.map((project) => (
					<div
						key={project.id}
						className="@container col-span-full lg:mt-0 @2xl:col-span-6"
					>
						<CourseCard {...project.card} />
					</div>
				))}
			</Grid>
		</>
	)
}

export default WorkHome
